import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Zap, Maximize, ChevronDown, Menu, X, Image as ImageIcon, Sparkles, User as UserIcon, LogOut } from 'lucide-react';
import { SignedIn, SignedOut, SignInButton, UserButton, useAuth } from "@clerk/clerk-react";
import { Link, useLocation } from 'react-router-dom';
import { useCurrency } from '../hooks/useCurrency';

export const Navbar: React.FC = () => {
  const location = useLocation();
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const drawerCloseButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { userId, getToken, signOut } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const [credits, setCredits] = useState<number | null>(null);

  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const currencyRef = useRef<HTMLDivElement | null>(null);

  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement | null>(null);

  const currencyOptions = useMemo(
    () => [
      { code: 'USD' as const, label: 'US Dollar', flag: '🇺🇸' },
      { code: 'CAD' as const, label: 'Canadian Dollar', flag: '🇨🇦' },
    ],
    []
  );

  const selectedCurrency = currencyOptions.find((c) => c.code === currency) ?? currencyOptions[0];

  useEffect(() => {
    const fetchCredits = async () => {
      if (!userId) return; // Only fetch if user is signed in

      try {
        const token = await getToken();
        const response = await fetch('/api/get-user-credits', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const c = typeof data.credits === 'number' ? data.credits : Number(data.credits || 0);
          setCredits(c);
        } else {
          console.error("Failed to fetch user credits:", response.status, response.statusText);
          setCredits(0); // Default to 0 on error
        }
      } catch (error) {
        console.error("Error fetching user credits:", error);
        setCredits(0); // Default to 0 on error
      }
    };

    fetchCredits();
  }, [userId, getToken]); // Re-fetch when userId or getToken changes

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const targetEl = e.target as HTMLElement | null;

      // Clerk's <UserButton /> popover is rendered in a portal outside of our accountRef.
      // Without this guard, any click inside the Clerk popover counts as "outside" and
      // closes our dropdown immediately, preventing actions like Sign out.
      const isInsideClerkUi = !!targetEl?.closest(
        '[data-clerk-root], [data-clerk-portal], [data-clerk-id], .cl-rootBox, .cl-userButtonPopover',
      );

      if (isCurrencyOpen && currencyRef.current && targetEl && !currencyRef.current.contains(targetEl)) {
        setIsCurrencyOpen(false);
      }

      if (isAccountOpen && !isInsideClerkUi && accountRef.current && targetEl && !accountRef.current.contains(targetEl)) {
        setIsAccountOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setIsCurrencyOpen(false);
      setIsAccountOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isCurrencyOpen, isAccountOpen]);

  // Mobile drawer close (Esc) + focus management.
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    // focus the close button on open
    setTimeout(() => drawerCloseButtonRef.current?.focus(), 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) return;
    // restore focus to menu button
    menuButtonRef.current?.focus();
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Desktop navbar */}
      <nav
        className="hidden lg:block glass border-b border-white/40 sticky top-0 z-[100] shadow-sm transition-all duration-300 pointer-events-auto"
        onClickCapture={(e) => {
          // TEMP DEBUG: If navigation “breaks” after upload, we want to know if events are being prevented.
          // eslint-disable-next-line no-console
          console.log('[Navbar click capture]', {
            target: (e.target as HTMLElement | null)?.tagName,
            defaultPrevented: e.defaultPrevented,
          });
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 pt-1">
            <div className="flex items-center">
              <Link to="/" className="flex items-center group">
                <img
                  src="/brand/logo.png"
                  alt="Storeylines"
                  className="h-12 w-12 object-contain"
                />
                <div className="ml-3 flex flex-col justify-center">
                  <span className="text-2xl font-display font-bold text-palette-4 tracking-tight group-hover:text-palette-3 transition-colors leading-none">
                    Storeylines
                  </span>
                  <span className="text-[10px] font-semibold text-palette-4/50 tracking-wide uppercase mt-1 hidden sm:block">
                    On-demand home portraits
                  </span>
                </div>
              </Link>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-palette-4/60">Currency</span>
                <div className="dropdown" ref={currencyRef}>
                  <button
                    type="button"
                    className="pill"
                    aria-haspopup="menu"
                    aria-expanded={isCurrencyOpen}
                    onClick={() => setIsCurrencyOpen((v) => !v)}
                    title="Select currency"
                  >
                    <span className="pill-emoji" aria-hidden="true">{selectedCurrency.flag}</span>
                    <span className="pill-label">{selectedCurrency.code}</span>
                    <ChevronDown className="w-4 h-4 text-palette-4/60" />
                  </button>

                  {isCurrencyOpen && (
                    <div className="dropdown-panel" role="menu" aria-label="Currency">
                      {currencyOptions.map((opt) => (
                        <button
                          key={opt.code}
                          type="button"
                          role="menuitemradio"
                          aria-checked={currency === opt.code}
                          aria-current={currency === opt.code}
                          className="dropdown-item"
                          onClick={() => {
                            setCurrency(opt.code as any);
                            setIsCurrencyOpen(false);
                          }}
                        >
                          <span className="pill-emoji" aria-hidden="true">{opt.flag}</span>
                          <span className="pill-label">{opt.code}</span>
                          <span className="text-xs text-palette-4/60 ml-auto">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Link
                to="/create"
                onClick={() => sessionStorage.setItem('resetFlow', 'true')}
                className={`relative text-palette-4/70 hover:text-palette-3 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                  location.pathname === '/create' ? 'text-palette-3 bg-palette-3/5' : ''
                }`}
              >
                Create
                <span className="absolute -top-2 -right-2 bg-palette-3/10 text-palette-3 text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm ring-1 ring-palette-3/20">
                  Free!
                </span>
              </Link>
              <Link to="/gallery" className="text-palette-4/70 hover:text-palette-3 px-3 py-2 rounded-md text-sm font-semibold transition-colors">
                Gallery
              </Link>
              <Link to="/pricing" className="text-palette-4/70 hover:text-palette-3 px-3 py-2 rounded-md text-sm font-semibold transition-colors">
                Pricing
              </Link>
              <Link to="/about" className="text-palette-4/70 hover:text-palette-3 px-3 py-2 rounded-md text-sm font-semibold transition-colors">
                About
              </Link>
              <div className="h-6 w-px bg-palette-2/30 mx-2"></div>

              <SignedOut>
                <SignInButton mode="modal">
                  <button className="btn btn-primary px-6 py-2.5 text-sm">Sign In</button>
                </SignInButton>
              </SignedOut>

              <SignedIn>
                <div className="dropdown" ref={accountRef}>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-haspopup="menu"
                    aria-expanded={isAccountOpen}
                    onClick={() => setIsAccountOpen((v) => !v)}
                    title="Account"
                    aria-label="Account"
                  >
                    <UserIcon className="h-5 w-5" />
                    {(credits ?? 0) > 0 && (
                      <span className="icon-badge" aria-hidden="true">
                        {credits ?? 0}
                      </span>
                    )}
                  </button>

                  {isAccountOpen && (
                    <div className="dropdown-panel" role="menu" aria-label="Account">
                      <div className="px-3 py-2">
                        <div className="text-xs font-semibold text-palette-4/60">Credits</div>
                        <div className="mt-2 grid gap-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 text-palette-4/80">
                              <Zap className="h-4 w-4 text-palette-5" /> Available
                            </span>
                            <span className="font-bold text-palette-4">{credits ?? 0}</span>
                          </div>
                        </div>
                      </div>

                      <div className="h-px bg-palette-2/20 my-2" />

                      <Link
                        to="/my-creations"
                        className="dropdown-item"
                        role="menuitem"
                        onClick={() => setIsAccountOpen(false)}
                      >
                        My Creations
                      </Link>

                      <Link
                        to="/my-orders"
                        className="dropdown-item"
                        role="menuitem"
                        onClick={() => setIsAccountOpen(false)}
                      >
                        My Orders
                      </Link>

                      <div className="h-px bg-palette-2/20 my-2" />

                      <button
                        type="button"
                        className="dropdown-item"
                        role="menuitem"
                        onClick={async () => {
                          setIsAccountOpen(false);
                          await signOut({ redirectUrl: '/' });
                        }}
                      >
                        <LogOut className="h-4 w-4" />
                        <span className="ml-2">Sign out</span>
                      </button>

                      <div className="px-3 py-2">
                        {/* Keep Clerk UI for account management, but don't rely on its popover for sign out */}
                        <UserButton />
                      </div>
                    </div>
                  )}
                </div>
              </SignedIn>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile top bar */}
      <nav className="lg:hidden glass border-b border-white/40 sticky top-0 z-[100] shadow-sm pointer-events-auto">
        <div className="px-4">
          <div className="flex items-center justify-between h-16 pt-1">
            <Link to="/" className="flex items-center group">
              <img
                src="/brand/logo.png"
                alt="Storeylines"
                className="h-10 w-10 object-contain"
              />
              <div className="ml-2 flex flex-col justify-center">
                <span className="text-xl font-display font-bold text-palette-4 tracking-tight leading-none">
                  Storeylines
                </span>
                <span className="text-[8px] font-semibold text-palette-4/50 tracking-wide uppercase mt-0.5">
                  On-demand home portraits
                </span>
              </div>
            </Link>

            <button
              ref={menuButtonRef}
              type="button"
              className="icon-btn"
              aria-label="Open menu"
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile bottom tab bar */}
      <div className="lg:hidden mobile-bottom-nav">
        <Link to="/create" onClick={() => sessionStorage.setItem('resetFlow', 'true')} className={`mobile-tab ${location.pathname === '/create' ? 'is-active' : ''}`} aria-label="Create">
          <Sparkles className="h-5 w-5" />
          <span className="mobile-tab-label">Create</span>
          <span className="absolute top-1 right-[calc(50%-10px)] bg-palette-3/10 text-palette-3 text-[8px] font-bold px-1 py-0.5 rounded-full shadow-sm ring-1 ring-palette-3/20">
            Free!
          </span>
        </Link>
        <Link to="/gallery" className={`mobile-tab ${location.pathname === '/gallery' ? 'is-active' : ''}`} aria-label="Gallery">
          <ImageIcon className="h-5 w-5" />
          <span className="mobile-tab-label">Gallery</span>
        </Link>
        <Link to="/pricing" className={`mobile-tab ${location.pathname === '/pricing' ? 'is-active' : ''}`} aria-label="Pricing">
          <Maximize className="h-5 w-5" />
          <span className="mobile-tab-label">Pricing</span>
        </Link>
        <button type="button" className="mobile-tab" aria-label="Menu" onClick={() => setIsMobileMenuOpen(true)}>
          <UserIcon className="h-5 w-5" />
          <span className="mobile-tab-label">Menu</span>
        </button>
      </div>

      {/* Mobile drawer */}
      {isMobileMenuOpen && (
        <div className="mobile-drawer" role="dialog" aria-modal="true" aria-label="Menu">
          <button className="mobile-drawer-backdrop" aria-label="Close menu" onClick={() => setIsMobileMenuOpen(false)} />

          <div className="mobile-drawer-panel">
            <div className="mobile-drawer-header">
              <div className="text-lg font-display font-bold text-palette-4">Menu</div>
              <button
                ref={drawerCloseButtonRef}
                type="button"
                className="icon-btn"
                aria-label="Close menu"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mobile-drawer-body">
              <div className="mobile-drawer-section">
                <Link to="/create" onClick={() => { sessionStorage.setItem('resetFlow', 'true'); setIsMobileMenuOpen(false); }} className="mobile-drawer-link relative">
                  Create
                  <span className="inline-flex ml-2 bg-palette-3/10 text-palette-3 text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm ring-1 ring-palette-3/20">
                    Free!
                  </span>
                </Link>
                <Link to="/gallery" className="mobile-drawer-link" onClick={() => setIsMobileMenuOpen(false)}>
                  Gallery
                </Link>
                <Link to="/pricing" className="mobile-drawer-link" onClick={() => setIsMobileMenuOpen(false)}>
                  Pricing
                </Link>
                <Link to="/about" className="mobile-drawer-link" onClick={() => setIsMobileMenuOpen(false)}>
                  About
                </Link>
                <SignedIn>
                  <Link to="/my-creations" className="mobile-drawer-link" onClick={() => setIsMobileMenuOpen(false)}>
                    My Creations
                  </Link>
                  <Link to="/my-orders" className="mobile-drawer-link" onClick={() => setIsMobileMenuOpen(false)}>
                    My Orders
                  </Link>
                </SignedIn>
              </div>

              <div className="mobile-drawer-section">
                <div className="text-xs font-semibold text-palette-4/60 mb-2">Currency</div>
                <div className="flex gap-2">
                  {currencyOptions.map((opt) => (
                    <button
                      key={opt.code}
                      type="button"
                      className={`pill ${currency === opt.code ? 'ring-2 ring-palette-3/40' : ''}`}
                      aria-pressed={currency === opt.code}
                      onClick={() => setCurrency(opt.code as any)}
                    >
                      <span className="pill-emoji" aria-hidden="true">{opt.flag}</span>
                      <span className="pill-label">{opt.code}</span>
                    </button>
                  ))}
                </div>
              </div>

              <SignedIn>
                <div className="mobile-drawer-section">
                  <div className="text-xs font-semibold text-palette-4/60 mb-2">Credits</div>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-palette-4/80">
                      <Zap className="h-4 w-4 text-palette-5" /> Available
                    </div>
                    <div className="text-sm font-bold text-palette-4">{credits ?? 0}</div>
                  </div>
                </div>
              </SignedIn>

              <div className="mobile-drawer-section">
                <SignedOut>
                  <SignInButton mode="modal">
                    <button className="btn btn-primary w-full">Sign In</button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <div className="flex items-center justify-between">
                    <div className="border-2 border-palette-1 rounded-full p-0.5">
                      <UserButton />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={async () => {
                          setIsMobileMenuOpen(false);
                          await signOut({ redirectUrl: '/' });
                        }}
                      >
                        Sign out
                      </button>
                      <button type="button" className="btn btn-ghost" onClick={() => setIsMobileMenuOpen(false)}>
                        Done
                      </button>
                    </div>
                  </div>
                </SignedIn>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export {};
